// features/onboarding/OnboardingPage.tsx — the three-step "Welcome" flow (1.24.00 B1).
//
//   (1) pick at least 3 topics you're into   → updateProfile({ interests })
//   (2) follow at least 5 people             → POST /users/follow-many/
//   (3) write your first post                → the composer (inline on desktop /
//                                              tablet, the bottom Sheet on mobile web)
//
// Every step is skippable, the whole flow is dismissible, and nothing here gates
// speech or reading. It never shows to existing members (grandfathered as done
// by migration) and it is resumable across devices because completion lives on
// the server (`onboardedAt`) — the CURRENT step is derived from real data
// (interests picked? following >= 5? posts >= 1?), never stored as a number.
//
// Under BareLayout: nothing else guards these children, so the page itself
// bounces a signed-out visitor to /signin?next=/onboarding. It also carries its
// own mobile compose Sheet — the shell's Sheet lives in ShellLayout, which is
// not mounted here.

import { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Panel, Button, Avatar, Callout, MetaRow, Sheet, useToast } from '@/ui';
import { Icon, type IconName } from '@/icons';
import {
  useSession,
  useViewport,
  usePosts,
  useTopics,
  useOnboardingSuggestions,
  useFollowMany,
  useFollowedHandles,
  useRequestedHandles,
  usePageTitle,
  ONBOARDING_MIN_INTERESTS,
  ONBOARDING_FOLLOW_TARGET,
} from '@/hooks';
import { useComposeSheetStore } from '@/core/stores/composeSheetStore';
import { track } from '@/core/lib/analytics';
import { normHandle } from '@/core/stores/followStore';
import { formatCount } from '@/components/formatCount';
import { Composer } from '@/features/home/Composer';
import { media } from '@/theme/media';
import type { SuggestedProfile } from '@/core/models/account';
import { InterestPicker } from './InterestPicker';
import { deferOnboarding } from './onboardingGate';

// ─── Styled ───────────────────────────────────────────────────────────────────

const Wrap = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 40px 24px;
  ${media.mobile`padding: 20px 12px 32px;`}
`;

const Card = styled(Panel).attrs({ $brackets: false })`
  width: 100%;
  max-width: 760px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 24px;
  ${media.mobile`padding: 18px 16px;`}
`;

const Head = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const Brand = styled.div`
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: 800;
  color: ${({ theme }) => theme.color.textPrimary};
  b { color: ${({ theme }) => theme.color.safety}; }
`;

const HeadActions = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const Title = styled.h1`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.xl};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const Lede = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
  line-height: 1.55;
`;

const StepRail = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  ${media.mobile`grid-template-columns: 1fr;`}
`;

const StepItem = styled.li<{ $state: 'done' | 'active' | 'todo' }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: ${({ theme, $state }) =>
    $state === 'active'
      ? `color-mix(in srgb, ${theme.color.accentCyan} 14%, transparent)`
      : 'transparent'};
  color: ${({ theme, $state }) => ($state === 'todo' ? theme.color.textMuted : theme.color.textPrimary)};
  font-size: ${({ theme }) => theme.fontSize.sm};
`;

const StepMark = styled.span<{ $done: boolean }>`
  display: inline-grid;
  place-items: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  flex: none;
  background: ${({ theme, $done }) =>
    $done ? theme.color.accentCyan : `color-mix(in srgb, ${theme.color.textMuted} 18%, transparent)`};
  color: ${({ theme, $done }) => ($done ? theme.color.onAccent : theme.color.textPrimary)};
  font-family: ${({ theme }) => theme.font.mono};
  font-variant-numeric: tabular-nums;
  font-size: ${({ theme }) => theme.fontSize.xs};
  line-height: 0;
`;

const Body = styled.section`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const StepTitle = styled.h2`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.lg};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const Hint = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;

const Counter = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.mono};
  font-variant-numeric: tabular-nums;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const PeopleGrid = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 10px;
`;

const PersonCardBtn = styled.button<{ $on: boolean; $following: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  text-align: left;
  padding: 10px 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: ${({ theme, $on, $following }) =>
    $on
      ? `color-mix(in srgb, ${theme.color.accentCyan} 14%, transparent)`
      : $following
        ? `color-mix(in srgb, ${theme.color.textMuted} 10%, transparent)`
        : 'transparent'};
  color: ${({ theme }) => theme.color.textPrimary};
  cursor: ${({ $following }) => ($following ? 'default' : 'pointer')};
  font-family: ${({ theme }) => theme.font.body};
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

const PersonInfo = styled.span`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

const PersonName = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PersonSub = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-variant-numeric: tabular-nums;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const PersonState = styled.span`
  display: inline-flex;
  line-height: 0;
  color: ${({ theme }) => theme.color.accentCyan};
`;

const Foot = styled.footer`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
`;

const GhostLink = styled.button`
  background: transparent;
  border: none;
  padding: 6px 8px;
  cursor: pointer;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
  border-radius: ${({ theme }) => theme.radius.sm};
  &:hover { color: ${({ theme }) => theme.color.textPrimary}; }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

// ─── Step model ───────────────────────────────────────────────────────────────

type StepId = 'interests' | 'follow' | 'post';

const STEPS: Array<{ id: StepId; label: string; icon: IconName }> = [
  { id: 'interests', label: 'Pick your topics', icon: 'sparkles' },
  { id: 'follow', label: 'Follow some people', icon: 'user-plus' },
  { id: 'post', label: 'Write your first post', icon: 'edit' },
];

// ─── Component ────────────────────────────────────────────────────────────────

export function OnboardingPage() {
  const { currentUser, isAuthenticated, isEmailVerified, updateProfile } = useSession();
  usePageTitle('Welcome / TruXSocial');
  const navigate = useNavigate();
  const toast = useToast();
  const viewport = useViewport();

  const { topics, minInterests, loading: topicsLoading } = useTopics();
  const {
    profiles,
    loading: peopleLoading,
    error: peopleError,
    refresh: refreshSuggestions,
  } = useOnboardingSuggestions();
  const followMany = useFollowMany();
  const followed = useFollowedHandles();
  const requested = useRequestedHandles();
  const { posts } = usePosts();

  // ── Derived initial step (real data, never a stored step number) ────────────
  // Captured ONCE on mount: currentUser is re-cached after every updateProfile,
  // and only the first derivation should pick the landing step.
  const [derivedDone] = useState(() => ({
    interests: (currentUser?.interests?.length ?? 0) >= ONBOARDING_MIN_INTERESTS,
    follow: (currentUser?.stats?.following ?? 0) >= ONBOARDING_FOLLOW_TARGET,
    post: (currentUser?.stats?.posts ?? 0) >= 1,
  }));
  // Land on the first step that is not already done (or the last one).
  const [step, setStep] = useState<StepId>(
    () => STEPS.find((s) => !derivedDone[s.id])?.id ?? 'post',
  );
  const [visited, setVisited] = useState<Set<StepId>>(() => new Set());

  // ── Step 1 state ────────────────────────────────────────────────────────────
  const [interests, setInterests] = useState<string[]>(() => currentUser?.interests ?? []);
  const [savingInterests, setSavingInterests] = useState(false);

  // ── Step 2 state ────────────────────────────────────────────────────────────
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [followingBusy, setFollowingBusy] = useState(false);
  const isFollowed = (p: SuggestedProfile) => followed.has(normHandle(p.handle));
  // A pending request to a PRIVATE account is as far as this member can get with
  // that account — the other side has to accept. So it reads as "Requested" and
  // counts toward the step, exactly like a follow. Without this the row (and the
  // counter) visibly un-does itself the moment the request succeeds.
  const isRequested = (p: SuggestedProfile) => requested.has(normHandle(p.handle));
  const isCommitted = (p: SuggestedProfile) => isFollowed(p) || isRequested(p);
  const committedInList = profiles.filter(isCommitted).length;
  const pendingSelected = [...selected].filter(
    (h) => !followed.has(normHandle(h)) && !requested.has(normHandle(h)),
  );
  const followCount = committedInList + pendingSelected.length;
  const followTarget = peopleLoading
    ? ONBOARDING_FOLLOW_TARGET
    : Math.max(1, Math.min(ONBOARDING_FOLLOW_TARGET, profiles.length || ONBOARDING_FOLLOW_TARGET));

  // ── Step 3 state ────────────────────────────────────────────────────────────
  const openSheet = useComposeSheetStore((s) => s.openSheet);
  const sheetOpen = useComposeSheetStore((s) => s.open);
  const closeSheet = useComposeSheetStore((s) => s.closeSheet);
  // The post ids that existed when the flow opened, captured once. State (with
  // a lazy initializer), not a ref: it is read during render to decide whether
  // a NEW post by this member has appeared, and reading a ref during render is
  // exactly the pattern that makes a component miss an update.
  const [initialPostIds] = useState(() => new Set(posts.map((p) => p.id)));
  const myHandle = currentUser ? normHandle(currentUser.handle) : null;
  const wroteFirstPost = useMemo(
    () =>
      !!myHandle &&
      posts.some((p) => !initialPostIds.has(p.id) && normHandle(p.handle) === myHandle),
    [posts, myHandle, initialPostIds],
  );

  const [finishing, setFinishing] = useState(false);

  async function finish() {
    if (finishing) return;
    setFinishing(true);
    // Belt and braces: the server stamps onboardedAt and re-caching the session
    // clears the redirect; the session flag covers the tab until that lands.
    deferOnboarding();
    const ok = await updateProfile({ onboardedAt: true });
    if (!ok) {
      toast("Couldn't save that. You can finish later from Settings");
    } else {
      // Funnel step (1.24.60, Umami) — the flow was stamped done server-side.
      track('onboarding-finished');
    }
    navigate('/', { replace: true });
  }

  // Completing a post on step 3 finishes the flow on its own — the member has
  // done the thing the step asks for, so making them also press Done would be
  // a needless extra tap. This genuinely reacts to state arriving from outside
  // this component (the post store, written by the Composer), which is what an
  // effect is for; the setState it reaches is the same `finish()` the Done
  // button calls. Same documented exemption style as the loader flags in
  // hooks/useOwnerList.ts.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- reacts to the post store (written by the Composer); finish() is the same path the Done button takes
    if (step === 'post' && wroteFirstPost) void finish();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, wroteFirstPost]);

  if (!isAuthenticated || !currentUser) {
    return <Navigate to={`/signin?next=${encodeURIComponent('/onboarding')}`} replace />;
  }

  function remindLater() {
    deferOnboarding();
    navigate('/', { replace: true });
  }

  function goTo(next: StepId) {
    setVisited((v) => new Set(v).add(step));
    setStep(next);
  }

  async function saveInterests() {
    if (interests.length < minInterests) return;
    setSavingInterests(true);
    const ok = await updateProfile({ interests });
    setSavingInterests(false);
    if (!ok) {
      toast('Something went wrong');
      return;
    }
    // Only NOW can the server rank step 2 by interest match — the mount fetch
    // asked before these interests existed. See useOnboardingSuggestions.refresh.
    refreshSuggestions();
    goTo('follow');
  }

  function toggleSelect(p: SuggestedProfile) {
    if (isCommitted(p)) return;
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(p.handle)) next.delete(p.handle);
      else next.add(p.handle);
      return next;
    });
  }

  async function followSelected() {
    if (pendingSelected.length === 0) return;
    setFollowingBusy(true);
    const r = await followMany(pendingSelected);
    setFollowingBusy(false);
    if (!r) {
      toast('Something went wrong');
      return;
    }
    setSelected(new Set());
    if (r.requested.length > 0) {
      toast(
        r.requested.length === 1
          ? `Follow request sent to ${r.requested[0]}`
          : `Follow requests sent to ${r.requested.length} private accounts`,
      );
    }
  }

  const stepIndex = STEPS.findIndex((s) => s.id === step);
  const isDone = (id: StepId) =>
    id === 'interests'
      ? interests.length >= minInterests && (visited.has('interests') || derivedDone.interests)
      : id === 'follow'
        ? committedInList >= followTarget || derivedDone.follow
        : wroteFirstPost || derivedDone.post;

  return (
    <Wrap>
      <Card>
        <Head>
          <div>
            <Brand>Tru<b>X</b>Social</Brand>
            <Title>Welcome, {currentUser.name}</Title>
          </div>
          <HeadActions>
            <GhostLink type="button" onClick={remindLater}>Remind me later</GhostLink>
            <GhostLink type="button" onClick={() => void finish()} disabled={finishing}>
              Skip for now
            </GhostLink>
          </HeadActions>
        </Head>
        <Lede>
          Three quick steps to make the place yours. Every one of them is optional. Nothing
          here changes what you can read or say.
        </Lede>

        <StepRail aria-label="Welcome steps">
          {STEPS.map((s, i) => {
            const state = s.id === step ? 'active' : isDone(s.id) ? 'done' : 'todo';
            return (
              <StepItem key={s.id} $state={state} aria-current={s.id === step ? 'step' : undefined}>
                <StepMark $done={state === 'done'} aria-hidden="true">
                  {state === 'done' ? <Icon name="check" size={14} /> : String(i + 1)}
                </StepMark>
                <Icon name={s.icon} size={16} />
                {s.label}
              </StepItem>
            );
          })}
        </StepRail>

        {!isEmailVerified && (
          <Callout $tone="info" role="status">
            Verify your email (we sent a link to your inbox) to follow people and post. You can
            still pick your topics now.
          </Callout>
        )}

        {step === 'interests' && (
          <Body aria-labelledby="onb-step-interests">
            <StepTitle id="onb-step-interests">What are you into?</StepTitle>
            <Hint>
              Pick at least {minInterests}. This shapes your For You feed and who we suggest.
              You can change it any time in Settings → Content.
            </Hint>
            {topicsLoading ? (
              <Hint role="status">Loading topics…</Hint>
            ) : (
              <InterestPicker topics={topics} value={interests} onChange={setInterests} />
            )}
            <Counter aria-live="polite">
              {interests.length} of {minInterests} picked
            </Counter>
            <Foot>
              <Button variant="ghost" onClick={() => goTo('follow')}>Skip this step</Button>
              <Button
                variant="primary"
                disabled={interests.length < minInterests || savingInterests}
                onClick={() => void saveInterests()}
              >
                {savingInterests ? 'Saving…' : 'Continue'}
              </Button>
            </Foot>
          </Body>
        )}

        {step === 'follow' && (
          <Body aria-labelledby="onb-step-follow">
            <StepTitle id="onb-step-follow">Follow some people</StepTitle>
            <Hint>
              Your Following feed is chronological and only shows who you choose. Pick a few to
              start. No algorithm fills the gap.
            </Hint>
            {peopleLoading && <Hint role="status">Finding people…</Hint>}
            {!peopleLoading && peopleError && <Hint role="status">{peopleError}</Hint>}
            {!peopleLoading && !peopleError && profiles.length === 0 && (
              <Hint role="status">Nobody to suggest yet. You can find people on Explore.</Hint>
            )}
            {profiles.length > 0 && (
              <PeopleGrid aria-label="Suggested people">
                {profiles.map((p) => {
                  const following = isFollowed(p);
                  const pendingRequest = !following && isRequested(p);
                  const on = selected.has(p.handle);
                  const label = following
                    ? 'Following'
                    : pendingRequest
                      ? 'Requested'
                      : on
                        ? 'Selected'
                        : 'Select';
                  return (
                    <li key={p.handle}>
                      <PersonCardBtn
                        type="button"
                        role="checkbox"
                        aria-checked={following || pendingRequest || on}
                        aria-disabled={following || pendingRequest || undefined}
                        aria-label={`${label} ${p.handle}`}
                        $on={on}
                        $following={following || pendingRequest}
                        onClick={() => toggleSelect(p)}
                      >
                        <Avatar name={p.name} size={36} avatarKey={p.av} />
                        <PersonInfo>
                          <PersonName>{p.name}</PersonName>
                          <PersonSub>
                            <MetaRow>
                              {p.handle}
                              {p.followerCount > 0 && `${formatCount(p.followerCount)} followers`}
                            </MetaRow>
                          </PersonSub>
                        </PersonInfo>
                        <PersonState aria-hidden="true">
                          {following ? (
                            <Icon name="user-check" size={16} />
                          ) : pendingRequest ? (
                            <Icon name="clock" size={16} />
                          ) : on ? (
                            <Icon name="check" size={16} />
                          ) : (
                            <Icon name="plus" size={16} />
                          )}
                        </PersonState>
                      </PersonCardBtn>
                    </li>
                  );
                })}
              </PeopleGrid>
            )}
            <Counter aria-live="polite" data-testid="follow-counter">
              {followCount} of {followTarget}
            </Counter>
            <Foot>
              <Button variant="ghost" onClick={() => goTo('post')}>Skip this step</Button>
              {pendingSelected.length > 0 ? (
                <Button
                  variant="primary"
                  disabled={followingBusy || !isEmailVerified}
                  onClick={() => void followSelected()}
                >
                  {followingBusy ? 'Following…' : `Follow selected (${pendingSelected.length})`}
                </Button>
              ) : (
                <Button
                  variant="primary"
                  disabled={committedInList < followTarget}
                  onClick={() => goTo('post')}
                >
                  Continue
                </Button>
              )}
            </Foot>
          </Body>
        )}

        {step === 'post' && (
          <Body aria-labelledby="onb-step-post">
            <StepTitle id="onb-step-post">Say something</StepTitle>
            <Hint>
              Your first post is where people find you. Say hello, share what brought you here,
              or skip it. The composer is always one tap away.
            </Hint>
            {viewport === 'mobile' ? (
              <Button variant="secondary" onClick={openSheet} disabled={!isEmailVerified}>
                <Icon name="edit" size={14} /> Write your first post
              </Button>
            ) : (
              <Composer />
            )}
            <Foot>
              <Button variant="primary" onClick={() => void finish()} disabled={finishing}>
                {finishing ? 'Finishing…' : 'Done'}
              </Button>
            </Foot>
          </Body>
        )}

        {stepIndex >= 0 && (
          <Counter aria-hidden="true">
            Step {stepIndex + 1} of {STEPS.length}
          </Counter>
        )}
      </Card>

      {/* Mobile-web compose sheet — ShellLayout's copy is not mounted under BareLayout. */}
      {viewport === 'mobile' && (
        <Sheet side="bottom" open={sheetOpen} onClose={closeSheet} title="New post">
          <Composer />
        </Sheet>
      )}
    </Wrap>
  );
}
